import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { NavLink, useNavigate, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Collapse from '@mui/material/Collapse';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Stepper from '@mui/material/Stepper';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import AltRouteOutlinedIcon from '@mui/icons-material/AltRouteOutlined';
import ExpandLessOutlinedIcon from '@mui/icons-material/ExpandLessOutlined';
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined';
import { DateField, Notice, PageHeader } from '@routes-plan/ui';
import { StandardModalBody, StandardModalFooter } from '../../../components/modales/StandardModal';
import { useResourcePermissions } from '../../../hooks/useResourcePermissions';
import FormActions from '../shared/FormActions';
import {
  INVENTORY_ROUTES,
  OfficeOption,
  RouteOption,
  ShowAlerts,
  apiErrorMessage,
  fetchOffices,
  fetchRoutes,
  formatMoney,
  newRequestId,
  priceFromUtilityAmount,
  priceFromUtilityPercent,
  utilityPercentOf,
} from '../shared/inventoryApi';
import { CompanyMoneyField, FieldGrid, FormFooter, IntField, PageBody, SaveButton, Section, fullRow, useInventoryAlert } from '../shared/ui';
import { nowOperationalDateString } from '../../../utils/operationalDateTime';
import { TextKey, useT } from '../../../i18n';
import { setPageTitle } from '../../../utils/pageTitle';

interface TypeStateOption {
  id: number;
  name: string;
}

type Field = 'office' | 'title' | 'price' | 'stockQty' | 'unitCost' | 'expirationDate' | 'stockRoute' | 'transferQty';
type Problem = { field: Field; key: TextKey; step: number };

/**
 * Crear un producto de una sede en pasos guiados (qué es → inventario inicial opcional)
 * o editarlo (con :id) en un solo formulario. El producto se presta en todas las rutas de su sede.
 */
interface ProductFormProps extends ShowAlerts {
  /** Producto a editar cuando se abre como modal (en página viene por la URL). */
  productId?: number;
  /** 'modal': dentro de StandardModal (composed); sin productId es el asistente de creación. */
  variant?: 'page' | 'modal';
  onClose?: () => void;
  /** Edición guardada (modal). */
  onSaved?: () => void;
  /** Producto creado (modal): para recargar el listado. */
  onCreated?: (productId: number) => void;
  /** Crear otro producto (modal): el padre vuelve a montar el asistente. */
  onCreateAnother?: () => void;
  /** En modal: abrir "Asignar rutas" de este producto (o del recién creado). */
  onOpenAssignments?: (productId?: number) => void;
}

const ProductForm: React.FC<ProductFormProps> = ({
  showLoading,
  hideLoading,
  productId,
  variant = 'page',
  onClose,
  onSaved,
  onCreated,
  onCreateAnother,
  onOpenAssignments,
}) => {
  const t = useT();
  const showAlert = useInventoryAlert();
  // Pasos del asistente de creación.
  const STEPS = [t('products.form.stepWhat'), t('products.form.stepStock')];
  const params = useParams();
  const id = productId != null ? String(productId) : params.id;
  const inModal = variant === 'modal';
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { canCreate, canUpdate } = useResourcePermissions(INVENTORY_ROUTES.products);
  const { canCreate: canCreateIncome } = useResourcePermissions(INVENTORY_ROUTES.incomes);
  const { canTransfer } = useResourcePermissions(INVENTORY_ROUTES.transfers);
  const allowed = isEdit ? canUpdate : canCreate;

  const [title, setTitle] = useState('');
  const [sku, setSku] = useState('');
  const [unit, setUnit] = useState('und');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  // Utilidad sobre el costo (informativa): se escribe el precio, el % o el $ y se calculan los otros.
  const [utilityPercent, setUtilityPercent] = useState('');
  const [utilityAmount, setUtilityAmount] = useState('');
  const [costPrice, setCostPrice] = useState('');
  const [minStock, setMinStock] = useState('');
  const [hasExpiration, setHasExpiration] = useState(false);
  const [shelfLifeDays, setShelfLifeDays] = useState('');
  const [typeStateId, setTypeStateId] = useState<number | ''>('');
  const [typeStates, setTypeStates] = useState<TypeStateOption[]>([]);
  const [moreOptions, setMoreOptions] = useState(false);

  // Sede dueña del producto.
  const [offices, setOffices] = useState<OfficeOption[]>([]);
  const [officeId, setOfficeId] = useState<number | ''>('');
  const [officeLocked, setOfficeLocked] = useState(false);

  // Paso 2: inventario inicial (opcional).
  const [withStock, setWithStock] = useState(false);
  const [stockQty, setStockQty] = useState('');
  const [unitCost, setUnitCost] = useState('');
  const [expirationDate, setExpirationDate] = useState('');
  const [sendToRoute, setSendToRoute] = useState(false);
  const [routes, setRoutes] = useState<RouteOption[]>([]);
  const [stockRoute, setStockRoute] = useState<number | ''>('');
  const [transferQty, setTransferQty] = useState('');

  const [step, setStep] = useState(0);
  const [errors, setErrors] = useState<Partial<Record<Field, string>>>({});
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState<{ productId: number; lines: string[]; warning?: string } | null>(null);
  const requestIds = useRef({ income: newRequestId('income'), transfer: newRequestId('transfer') });
  if (!inModal) setPageTitle(isEdit ? 'products.editTitle' : 'products.newTitle');

  useEffect(() => {
    const load = async () => {
      showLoading();
      try {
        const [{ data: states }, officeList] = await Promise.all([axios.get('type-states'), fetchOffices()]);
        setTypeStates(Array.isArray(states?.data) ? states.data : []);
        setOffices(officeList);
        if (!isEdit && officeList.length === 1) setOfficeId(officeList[0].id);
        if (isEdit) {
          const { data: p } = await axios.get(`products/${id}`);
          setTitle(p.title ?? '');
          setSku(p.sku ?? '');
          setUnit(p.unit ?? '');
          setDescription(p.description ?? '');
          setPrice(p.price != null ? String(p.price) : '');
          const cost = p.costPrice != null ? Number(p.costPrice) : 0;
          setCostPrice(p.costPrice != null ? String(cost) : '');
          fillUtility(cost, Number(p.price || 0));
          setMinStock(p.minStock != null ? String(p.minStock) : '');
          setHasExpiration(Boolean(p.hasExpiration));
          setShelfLifeDays(p.shelfLifeDays != null ? String(p.shelfLifeDays) : '');
          setTypeStateId(p.status?.id ?? '');
          setOfficeId(p.officeId ?? '');
          setOfficeLocked(Boolean(p.officeId));
          setMoreOptions(true);
        }
      } catch (error) {
        showAlert(apiErrorMessage(error, t('products.form.loadError')), 'error');
      } finally {
        hideLoading();
      }
    };
    load();
  }, [id, isEdit, showAlert, showLoading, hideLoading, t]);

  // Las rutas a las que se puede enviar el inventario inicial: las de la sede del producto.
  useEffect(() => {
    setStockRoute('');
    if (!officeId) {
      setRoutes([]);
      return;
    }
    fetchRoutes(Number(officeId)).then(setRoutes).catch(() => setRoutes([]));
  }, [officeId]);

  // Costo · Precio · Utilidad (% y $): el precio manda; la utilidad se calcula sobre el costo.
  function fillUtility(cost: number, priceValue: number) {
    const pct = utilityPercentOf(cost, priceValue);
    setUtilityPercent(pct === null ? '' : String(pct));
    setUtilityAmount(pct === null ? '' : String(Math.round(priceValue - cost)));
  }
  const changeCost = (value: string) => {
    setCostPrice(value);
    fillUtility(Number(value), Number(price));
  };
  const changePrice = (value: string) => {
    setPrice(value);
    clearError('price');
    fillUtility(Number(costPrice), Number(value));
  };
  const changeUtilityPercent = (value: string) => {
    setUtilityPercent(value);
    if (value === '') return;
    const next = priceFromUtilityPercent(Number(costPrice), Number(value));
    if (next === null) return;
    setPrice(String(next));
    setUtilityAmount(String(next - Number(costPrice)));
  };
  const changeUtilityAmount = (value: string) => {
    setUtilityAmount(value);
    if (value === '') return;
    const next = priceFromUtilityAmount(Number(costPrice), Number(value));
    if (next === null) return;
    setPrice(String(next));
    const pct = utilityPercentOf(Number(costPrice), next);
    setUtilityPercent(pct === null ? '' : String(pct));
  };
  const hasCost = Number(costPrice) > 0;
  const clearError = (field: Field) => setErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev));

  const officeName = offices.find((o) => o.id === officeId)?.name ?? '';

  const productPayload = (): Record<string, unknown> => ({
    title: title.trim(),
    sku: sku.trim() || null,
    unit: unit.trim() || null,
    description,
    price: Number(price),
    costPrice: costPrice === '' ? null : Number(costPrice),
    minStock: minStock === '' ? null : Number(minStock),
    hasExpiration: hasExpiration,
    shelfLifeDays: hasExpiration && shelfLifeDays !== '' ? Number(shelfLifeDays) : null,
    statusId: typeStateId === '' ? null : typeStateId,
    ...(officeId && !officeLocked ? { officeId: officeId } : {}),
  });

  /** Errores de un paso, por campo (se muestran debajo de cada campo). */
  const problemsOf = (index: number): Problem[] => {
    const list: Problem[] = [];
    if (index === 0) {
      if (!officeId && !(isEdit && !officeLocked)) list.push({ field: 'office', key: 'products.form.errOffice', step: 0 });
      if (!title.trim()) list.push({ field: 'title', key: 'products.form.errName', step: 0 });
      if (!(Number(price) > 0)) list.push({ field: 'price', key: 'products.form.errPrice', step: 0 });
    }
    if (index === 1 && withStock) {
      if (!(Number(stockQty) > 0)) list.push({ field: 'stockQty', key: 'products.form.errQty', step: 1 });
      if (Number(unitCost) < 0 || unitCost === '') list.push({ field: 'unitCost', key: 'products.form.errUnitCost', step: 1 });
      if (hasExpiration && !expirationDate && !(Number(shelfLifeDays) > 0)) list.push({ field: 'expirationDate', key: 'products.form.errExpiry', step: 1 });
      if (sendToRoute) {
        if (!stockRoute) list.push({ field: 'stockRoute', key: 'products.form.errRoute', step: 1 });
        if (!(Number(transferQty) > 0) || Number(transferQty) > Number(stockQty)) {
          list.push({ field: 'transferQty', key: 'products.form.errTransferQty', step: 1 });
        }
      }
    }
    return list;
  };

  /** Marca los errores; devuelve true si el paso está bien. */
  const check = (indexes: number[]) => {
    const problems = indexes.flatMap(problemsOf);
    setErrors(Object.fromEntries(problems.map((p) => [p.field, t(p.key)])));
    if (problems.length) {
      setStep(problems[0].step);
      return false;
    }
    return true;
  };

  const next = () => {
    if (!check([step])) return;
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const saveEdit = async () => {
    if (!allowed || !check([0])) return;
    setSaving(true);
    showLoading();
    try {
      await axios.put(`products/${id}`, productPayload());
      showAlert(t('products.form.updated'), 'success');
      if (inModal) onSaved?.();
      else navigate(INVENTORY_ROUTES.products);
    } catch (error) {
      showAlert(apiErrorMessage(error, t('products.form.saveError')), 'error');
    } finally {
      setSaving(false);
      hideLoading();
    }
  };

  const finishCreate = async () => {
    if (!allowed || !check([0, 1])) return;
    setSaving(true);
    showLoading();
    const lines: string[] = [];
    let createdId: number | null = null;
    try {
      const { data: created } = await axios.post('products', productPayload());
      createdId = Number(created?.id);
      lines.push(
        hasCost && utilityPercent !== ''
          ? t('products.form.lineCreatedWithUtility', {
              name: title.trim(),
              office: officeName,
              price: formatMoney(Number(price)),
              percent: utilityPercent,
              amount: formatMoney(Number(utilityAmount)),
            })
          : t('products.form.lineCreated', { name: title.trim(), office: officeName, price: formatMoney(Number(price)) }),
      );
      lines.push(t('products.form.lineLendsAll', { office: officeName }));
      if (withStock && createdId) {
        await axios.post('stock-receipts', {
          officeId: officeId,
          date: nowOperationalDateString(),
          observation: t('products.form.initialIncomeNote'),
          clientRequestId: requestIds.current.income,
          items: [
            {
              productId: createdId,
              quantity: Number(stockQty),
              unitCost: Number(unitCost),
              ...(hasExpiration && expirationDate ? { expirationDate: expirationDate } : {}),
            },
          ],
        });
        lines.push(t('products.form.lineIncome', { qty: stockQty, office: officeName }));
        if (sendToRoute && stockRoute) {
          const routeName = routes.find((c) => c.id === stockRoute)?.name ?? '';
          await axios.post('stock-transfers', {
            origin: { officeId: officeId },
            destination: { routeId: stockRoute },
            date: nowOperationalDateString(),
            observation: t('products.form.initialTransferNote'),
            clientRequestId: requestIds.current.transfer,
            items: [{ productId: createdId, quantity: Number(transferQty) }],
          });
          lines.push(t('products.form.lineTransfer', { qty: transferQty, route: routeName }));
        }
      }
      const warning = !withStock
        ? t('products.form.warnNoStock')
        : !sendToRoute
          ? t('products.form.warnNoTransfer')
          : undefined;
      setResult({ productId: createdId, lines, warning });
      showAlert(t('products.createdTitle'), 'success');
      if (inModal && createdId) onCreated?.(createdId);
    } catch (error) {
      if (createdId) {
        setResult({
          productId: createdId,
          lines,
          warning: t('products.form.warnStockFailed', { error: apiErrorMessage(error, t('products.form.unknownError')) }),
        });
      } else {
        showAlert(apiErrorMessage(error, t('products.form.saveError')), 'error');
      }
    } finally {
      setSaving(false);
      hideLoading();
    }
  };

  // ---------------------------------------------------------------- secciones

  const showOfficePicker = !officeLocked && (offices.length > 1 || isEdit);
  const dataSection = (
    <Section plain={inModal} title={t('products.form.sectionData')}>
      <FieldGrid>
        {showOfficePicker ? (
          <TextField
            select
            fullWidth
            required
            label={t('common.office')}
            value={officeId === '' ? '' : String(officeId)}
            onChange={(e) => {
              setOfficeId(Number(e.target.value) || '');
              clearError('office');
            }}
            disabled={!allowed}
            error={Boolean(errors.office)}
            helperText={errors.office ?? (isEdit ? t('products.form.officeHelpLegacy') : t('products.form.officeHelp'))}
          >
            {offices.map((o) => (
              <MenuItem key={o.id} value={String(o.id)}>
                {o.name}
              </MenuItem>
            ))}
          </TextField>
        ) : (
          <Box sx={fullRow}>
            <Typography variant="body2" color="text.secondary">
              {t('products.form.officeLabel')}{' '}
              <Box component="strong" sx={{ color: 'text.primary' }}>
                {officeName || '—'}
              </Box>{' '}
              {t('products.form.lendsInAllShort')}
            </Typography>
          </Box>
        )}
        <TextField
          fullWidth
          required
          label={t('common.name')}
          value={title}
          onChange={(e) => {
            setTitle(e.target.value);
            clearError('title');
          }}
          disabled={!allowed}
          error={Boolean(errors.title)}
          helperText={errors.title}
          sx={showOfficePicker ? undefined : fullRow}
        />
        <TextField fullWidth label={t('products.form.skuCode')} value={sku} onChange={(e) => setSku(e.target.value)} disabled={!allowed} />
        <TextField fullWidth label={t('products.unit')} placeholder={t('products.form.unitPlaceholder')} value={unit} onChange={(e) => setUnit(e.target.value)} disabled={!allowed} />
        <TextField
          fullWidth
          multiline
          minRows={2}
          label={t('products.form.description')}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          disabled={!allowed}
          sx={fullRow}
        />
      </FieldGrid>
    </Section>
  );

  const priceSection = (
    <Section plain={inModal} title={t('products.form.sectionPrice')} description={t('products.form.pricingHelp')}>
      <FieldGrid md={2} lg={inModal ? 2 : 4}>
        <CompanyMoneyField fullWidth label={t('products.cost')} helperText={t('products.form.costHelp')} value={costPrice} onChange={changeCost} disabled={!allowed} />
        <CompanyMoneyField
          fullWidth
          required
          label={t('products.price')}
          value={price}
          onChange={changePrice}
          disabled={!allowed}
          error={Boolean(errors.price)}
          helperText={errors.price ?? t('products.form.priceHelp')}
        />
        <TextField
          fullWidth
          label={t('products.utilityPercent')}
          value={utilityPercent}
          onChange={(e) => changeUtilityPercent(e.target.value.replace(/[^\d.,-]/g, '').replace(',', '.'))}
          disabled={!allowed || !hasCost}
          helperText={hasCost ? t('products.form.utilityHelp') : t('products.form.writeCost')}
          inputProps={{ inputMode: 'decimal', style: { textAlign: 'right' } }}
          InputProps={{ endAdornment: <Typography color="text.secondary">%</Typography> }}
        />
        <CompanyMoneyField
          fullWidth
          label={t('products.utilityAmount')}
          value={utilityAmount}
          onChange={changeUtilityAmount}
          disabled={!allowed || !hasCost}
          helperText={hasCost ? t('products.form.utilityAmountHelp') : t('products.form.writeCost')}
        />
      </FieldGrid>
    </Section>
  );

  const moreSection = (
    <Section
      plain={inModal}
      title={t('products.form.sectionMore')}
      description={moreOptions ? undefined : t('products.form.moreHelp')}
      actions={
        <Button
          size="small"
          onClick={() => setMoreOptions((v) => !v)}
          endIcon={moreOptions ? <ExpandLessOutlinedIcon /> : <ExpandMoreOutlinedIcon />}
          aria-expanded={moreOptions}
        >
          {moreOptions ? t('products.form.hideMoreOptions') : t('products.form.showMoreOptions')}
        </Button>
      }
    >
      <Collapse in={moreOptions} unmountOnExit>
        <FieldGrid>
          <IntField fullWidth label={t('products.form.minStock')} helperText={t('products.form.minStockHelp')} value={minStock} onChange={setMinStock} disabled={!allowed} />
          <TextField
            select
            fullWidth
            label={t('common.status')}
            value={typeStateId === '' ? '' : String(typeStateId)}
            onChange={(e) => setTypeStateId(Number(e.target.value) || '')}
            disabled={!allowed}
            SelectProps={{ displayEmpty: true }}
            InputLabelProps={{ shrink: true }}
          >
            <MenuItem value="">{t('products.form.defaultState')}</MenuItem>
            {typeStates.map((s) => (
              <MenuItem key={s.id} value={String(s.id)}>
                {s.name}
              </MenuItem>
            ))}
          </TextField>
          <FormControlLabel
            control={<Switch checked={hasExpiration} onChange={(e) => setHasExpiration(e.target.checked)} disabled={!allowed} />}
            label={t('products.form.hasExpiration')}
          />
          <IntField
            fullWidth
            label={t('products.form.shelfLife')}
            helperText={t('products.form.shelfLifeHelp')}
            value={shelfLifeDays}
            onChange={setShelfLifeDays}
            disabled={!allowed || !hasExpiration}
          />
        </FieldGrid>
      </Collapse>
    </Section>
  );

  const whatSection = (
    <Stack spacing={inModal ? 3 : 2} divider={inModal ? <Divider flexItem /> : undefined}>
      {dataSection}
      {priceSection}
      {moreSection}
    </Stack>
  );

  const canAddStock = canCreateIncome;
  const stockSection = (
    <Section plain={inModal} title={t('products.form.stepStock')} description={canAddStock ? t('products.form.stockHelp') : undefined}>
      {!canAddStock ? (
        <Notice severity="info">{t('products.form.noIncomePermission')}</Notice>
      ) : (
        <Stack spacing={2}>
          <FormControlLabel
            control={
              <Switch
                checked={withStock}
                onChange={(e) => {
                  setWithStock(e.target.checked);
                  // El costo por unidad arranca con el costo del producto.
                  if (e.target.checked && unitCost === '' && costPrice !== '') setUnitCost(costPrice);
                }}
              />
            }
            label={t('products.form.withStockNow')}
          />
          <Collapse in={withStock} unmountOnExit>
            <FieldGrid>
              <TextField fullWidth label={t('products.form.intoOffice')} value={officeName} InputProps={{ readOnly: true }} sx={fullRow} />
              <IntField
                fullWidth
                required
                label={t('products.form.units')}
                value={stockQty}
                onChange={(v) => {
                  setStockQty(v);
                  clearError('stockQty');
                }}
                error={Boolean(errors.stockQty)}
                helperText={errors.stockQty}
              />
              <CompanyMoneyField
                fullWidth
                required
                label={t('products.form.unitCost')}
                value={unitCost}
                onChange={(v) => {
                  setUnitCost(v);
                  clearError('unitCost');
                }}
                error={Boolean(errors.unitCost)}
                helperText={errors.unitCost}
              />
              {hasExpiration && (
                <DateField
                  fullWidth
                  label={t('products.form.expiresOn')}
                  value={expirationDate || null}
                  onChange={(v) => {
                    setExpirationDate(v ?? '');
                    clearError('expirationDate');
                  }}
                  error={Boolean(errors.expirationDate)}
                  helperText={errors.expirationDate ?? (Number(shelfLifeDays) > 0 ? t('products.form.expiresEmptyHelp', { days: shelfLifeDays }) : undefined)}
                />
              )}
              {canTransfer && (
                <FormControlLabel
                  sx={fullRow}
                  control={<Checkbox checked={sendToRoute} onChange={(e) => setSendToRoute(e.target.checked)} disabled={!officeId} />}
                  label={t('products.form.sendToRoute')}
                />
              )}
              {canTransfer && sendToRoute && (
                <>
                  <TextField
                    select
                    fullWidth
                    required
                    label={t('common.route')}
                    value={stockRoute === '' ? '' : String(stockRoute)}
                    onChange={(e) => {
                      setStockRoute(Number(e.target.value) || '');
                      clearError('stockRoute');
                    }}
                    error={Boolean(errors.stockRoute)}
                    helperText={errors.stockRoute}
                  >
                    {routes.map((c) => (
                      <MenuItem key={c.id} value={String(c.id)}>
                        {c.name}
                      </MenuItem>
                    ))}
                  </TextField>
                  <IntField
                    fullWidth
                    required
                    label={t('products.unitsToSend')}
                    value={transferQty}
                    onChange={(v) => {
                      setTransferQty(v);
                      clearError('transferQty');
                    }}
                    error={Boolean(errors.transferQty)}
                    helperText={errors.transferQty}
                  />
                </>
              )}
            </FieldGrid>
          </Collapse>
          {!withStock && (
            <Typography variant="body2" color="text.secondary">
              {t('products.form.laterHelp')}
            </Typography>
          )}
        </Stack>
      )}
    </Section>
  );

  const stepper = (
    <Stepper activeStep={step} alternativeLabel={isMobile} sx={{ mb: 3 }}>
      {STEPS.map((label) => (
        <Step key={label}>
          <StepLabel>{label}</StepLabel>
        </Step>
      ))}
    </Stepper>
  );

  const resultView = result && (
    <Stack spacing={1.5}>
      {result.lines.map((line) => (
        <Notice key={line} severity="success">
          {line}
        </Notice>
      ))}
      {result.warning && <Notice severity="warning">{result.warning}</Notice>}
    </Stack>
  );

  const createButtons = (
    <>
      {step > 0 && <Button onClick={() => setStep((s) => s - 1)}>{t('products.form.back')}</Button>}
      {step < STEPS.length - 1 ? (
        <Button variant="contained" onClick={next}>
          {t('products.form.next')}
        </Button>
      ) : (
        <SaveButton allowed={allowed} deniedTooltip={t('common.noPermissionCreate')} loading={saving} onClick={finishCreate}>
          {t('products.form.create')}
        </SaveButton>
      )}
    </>
  );

  const crumbs = (current: string) => [
    { label: t('products.list.title'), onClick: () => navigate(INVENTORY_ROUTES.products) },
    { label: current },
  ];

  // ---------------------------------------------------------------- vistas

  if (isEdit && inModal) {
    return (
      <>
        <StandardModalBody>
          <Box component="form" noValidate onSubmit={(e) => e.preventDefault()}>
            {whatSection}
          </Box>
        </StandardModalBody>
        <StandardModalFooter>
          <Button color="inherit" variant="outlined" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" justifyContent="flex-end">
            {onOpenAssignments && (
              <Button variant="outlined" startIcon={<AltRouteOutlinedIcon />} onClick={() => onOpenAssignments()}>
                {t('products.assignRoute')}
              </Button>
            )}
            <SaveButton allowed={allowed} deniedTooltip={t('common.noPermissionEdit')} loading={saving} onClick={saveEdit}>
              {t('common.save')}
            </SaveButton>
          </Stack>
        </StandardModalFooter>
      </>
    );
  }

  if (isEdit) {
    return (
      <PageBody>
        <PageHeader
          title={t('products.editTitle')}
          description={title || undefined}
          breadcrumbs={crumbs(t('products.editTitle'))}
          actions={
            <Button variant="outlined" startIcon={<AltRouteOutlinedIcon />} component={NavLink} to={`${INVENTORY_ROUTES.products}/${id}/assignments`}>
              {t('products.assignRoute')}
            </Button>
          }
        />
        <Box component="form" noValidate onSubmit={(e) => e.preventDefault()} sx={{ display: 'grid', gap: 2, maxWidth: 1100, width: '100%' }}>
          {whatSection}
          <FormActions allowed={allowed} deniedTooltip={t('common.noPermissionEdit')} onSave={saveEdit} cancelTo={INVENTORY_ROUTES.products} loading={saving} />
        </Box>
      </PageBody>
    );
  }

  if (inModal && result) {
    return (
      <>
        <StandardModalBody>{resultView}</StandardModalBody>
        <StandardModalFooter>
          <Button color="inherit" variant="outlined" onClick={onClose}>
            {t('common.close')}
          </Button>
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" justifyContent="flex-end">
            <Button onClick={onCreateAnother}>{t('products.createAnother')}</Button>
            {onOpenAssignments && (
              <Button variant="outlined" startIcon={<AltRouteOutlinedIcon />} onClick={() => onOpenAssignments(result.productId)}>
                {t('products.assignRoute')}
              </Button>
            )}
            <Button variant="contained" component={NavLink} to={`${INVENTORY_ROUTES.products}/by-route`}>
              {t('products.viewByRoute')}
            </Button>
          </Stack>
        </StandardModalFooter>
      </>
    );
  }

  if (inModal) {
    // Asistente de creación dentro del modal estándar.
    return (
      <>
        <StandardModalBody>
          {stepper}
          <Box component="form" noValidate onSubmit={(e) => e.preventDefault()}>
            {step === 0 && whatSection}
            {step === 1 && stockSection}
          </Box>
        </StandardModalBody>
        <StandardModalFooter>
          <Button color="inherit" variant="outlined" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Stack direction="row" spacing={1}>
            {createButtons}
          </Stack>
        </StandardModalFooter>
      </>
    );
  }

  if (result) {
    return (
      <PageBody>
        <PageHeader title={t('products.createdTitle')} breadcrumbs={crumbs(t('products.createdTitle'))} />
        <Section>
          {resultView}
          <Divider sx={{ my: 2 }} />
          <FormFooter>
            <Button color="inherit" component={NavLink} to={INVENTORY_ROUTES.products}>
              {t('products.form.goToList')}
            </Button>
            <Button onClick={() => navigate(0)}>{t('products.createAnother')}</Button>
            <Button variant="outlined" component={NavLink} to={`${INVENTORY_ROUTES.products}/${result.productId}/assignments`}>
              {t('products.assignRoute')}
            </Button>
            <Button variant="contained" component={NavLink} to={`${INVENTORY_ROUTES.products}/by-route`}>
              {t('products.viewByRoute')}
            </Button>
          </FormFooter>
        </Section>
      </PageBody>
    );
  }

  return (
    <PageBody>
      <PageHeader title={t('products.newTitle')} description={t('products.form.newDescription')} breadcrumbs={crumbs(t('products.newTitle'))} />
      <Box component="form" noValidate onSubmit={(e) => e.preventDefault()} sx={{ display: 'grid', gap: 2, maxWidth: 1100, width: '100%' }}>
        <Section>{stepper}</Section>
        {step === 0 && whatSection}
        {step === 1 && stockSection}
        <FormFooter>
          <Button color="inherit" variant="outlined" component={NavLink} to={INVENTORY_ROUTES.products}>
            {t('common.cancel')}
          </Button>
          {createButtons}
        </FormFooter>
      </Box>
    </PageBody>
  );
};

export default ProductForm;
